<template>
  <div class="flex justify-between" :key="tenant">
    <div class="flex flex-wrap gap-1 items-center">
      <Component
        v-for="filter in activeFilters"
        :modelValue="getFilterState(filter.id)"
        :is="getComponent(filter.id)"
        :key="filter.id"
        :open="filter.open"
        :name="filter.label"
        :includeAssets="includeAssets"
        :tenant="tenant ?? []"
        :showIncludeAssets="type !== 'assets'"
        @update:open="handleOpenActiveFilters({ value: $event, filter })"
        @update:modelValue="handleChange({ values: $event, filter })"
        @update:assets="handleUpdateAssets($event)"
        @update:includeAssets="handleUpdateIncludeAssets($event)"
        @removeFilter="handleRemoveActiveFilter(filter)"
      />
      <Main
        :type="type"
        :activeFilters="activeFilters"
        :tenant="tenant ?? []"
        :open="isMainFilterOpen"
        @update:open="isMainFilterOpen = $event"
        @update:modelValue="handleActiveFilters"
      />
      <Button
        v-if="!isEmpty(activeFilters)"
        label="Clear"
        variant="tertiary"
        size="xs"
        type="gray"
        @click="handleRemoveActiveFilters"
      />
    </div>
    <ViewCustomActions
      :currentView="currentView"
      :type="type"
      :views="views"
      :filterStates="filterStates"
      @cancel="handleCloseViewFilters"
      @addView="handleAddView($event)"
      @updateView="handleUpdateView($event)"
    />
  </div>
</template>

<script setup>
// Vue and utility imports
import { onMounted, ref, watchEffect, computed } from "vue";
import { first, isEmpty, remove } from "lodash";
import { storeToRefs } from "pinia";

// Component imports
import { ViewCustomActions } from "@/SharedComponents/PageHeader/Partials";
import { Button } from "@/SharedComponents/Buttons";

// Filter imports
import { Space } from "@/SharedComponents/Filters/Partials/Space";
import { Asset } from "@/SharedComponents/Filters/Partials/Asset";
import { AssetGroup } from "@/SharedComponents/Filters/Partials/AssetGroup";
import { Assignee } from "@/SharedComponents/Filters/Partials/Assignee";
import { Priority } from "@/SharedComponents/Filters/Partials/Priority";
import { Category } from "@/SharedComponents/Filters/Partials/Category";
import { Brand } from "@/SharedComponents/Filters/Partials/Brand";
import { Supplier } from "@/SharedComponents/Filters/Partials/Supplier";
import { Tag } from "@/SharedComponents/Filters/Partials/Tag";
import { Visibility } from "@/SharedComponents/Filters/Partials/Visibility";
import { TaskType } from "@/SharedComponents/Filters/Partials/TaskType";
import { Status } from "@/SharedComponents/Filters/Partials/Status";
import { Recurrence } from "@/SharedComponents/Filters/Partials/Recurrence";
import {
  DueDate,
  CompletedDate,
  CreatedDate,
  ExpirationDate,
  NotificationDate,
  PurchasedDate,
} from "@/SharedComponents/Filters/Partials/Date";
import {
  Main,
  FilterType,
  useMain,
} from "@/SharedComponents/Filters/Partials/Main";

// Composable imports
import { useFilters } from "@/Composables/useFilters";

const emit = defineEmits(["addView", "closeViewFilters", "updateView"]);

// Props definition
const props = defineProps({
  currentView: {
    type: Object,
    required: true,
  },
  views: {
    type: Array,
    required: true,
  },
  type: {
    type: String,
    required: true,
  },
});

// Computed props
const tenant = computed(() => {
  if (props.type === "recurring_tasks") {
    return [];
  }
  return globalFilterValues.value.tenant;
});

// Store and state management
const globalFilterStore = useFilters("global");
const { filterValues: globalFilterValues } = storeToRefs(globalFilterStore);
const filterStore = useFilters(props.type);
const { filterStates } = storeToRefs(filterStore);
const { handleChangeFilter } = filterStore;
const { optionGroups } = useMain(tenant.value, props.type);

// Reactive state
const includeAssets = ref(false);
const activeFilters = ref([]);
const isMainFilterOpen = ref(false);

// Lifecycle hooks
onMounted(() => {
  handleInitialiseActiveFilters();
});

/**
 * Watches for changes in the isMainFilterOpen value and updates the active filters accordingly.
 * When the main filter is opened, all other active filters are closed.
 */
watchEffect(() => {
  if (isMainFilterOpen.value) {
    activeFilters.value.forEach((filter) => {
      filter.open = false;
    });
  }
});

/**
 * Handles opening and closing of active filters
 * @param {Object} param0 - Object containing value and filter
 * @param {boolean} param0.value - Whether to open or close the filter
 * @param {Object} param0.filter - The filter to update
 */
const handleOpenActiveFilters = ({ value, filter }) => {
  activeFilters.value.forEach((item) => {
    item.open = item.id !== filter.id ? false : value ? value : true;
  });

  isMainFilterOpen.value = false;

  filter.open = value;
};

/**
 * Updates the includeAssets value
 * @param {boolean} value - New value for includeAssets
 */
const handleUpdateIncludeAssets = (value) => {
  includeAssets.value = value;
};

/**
 * Applies view filters by resetting and reinitializing active filters
 */
const handleApplyViewFilters = () => {
  activeFilters.value = [];
  setTimeout(() => handleInitialiseActiveFilters());
};

/**
 * Initializes the active filters based on the current filter states.
 *
 * This function performs the following steps:
 * 1. Flattens all options from optionGroups into a single array.
 * 2. Creates a Map of filter options for quick lookup.
 * 3. Iterates through the current filter states and creates active filter objects.
 * 4. Handles the special case of "linked_to" filter by creating separate entries for SPACES and ASSETS.
 * 5. For non-empty filter values, creates an active filter object if the filter exists in the options.
 *
 * @function
 * @name handleInitialiseActiveFilters
 */
const handleInitialiseActiveFilters = () => {
  const allOptions = optionGroups.flatMap((group) => group.options);
  const filterMap = new Map(allOptions.map((option) => [option.id, option]));

  activeFilters.value = Object.entries(filterStates.value).flatMap(
    ([key, value]) => {
      if (key === "linked_to") {
        return [FilterType.SPACES, FilterType.ASSETS].map((filterType) => {
          const filter = filterMap.get(filterType);
          return filter
            ? {
                id: filterType,
                open: false,
                label: filter.label,
                values: value,
              }
            : [];
        });
      } else if (!isEmpty(value)) {
        const filter = filterMap.get(key);
        return filter
          ? [{ id: key, open: false, label: filter.label, values: value }]
          : [];
      }
      return [];
    }
  );
};

/**
 * Adds a new active filter to the list
 * @param {Object} filter - The filter to be added
 */
const handleActiveFilters = (filter) => {
  activeFilters.value.push({
    ...first(filter),
    open: true,
    values: [],
  });
};

/**
 * Clear the filter states and remove all active filters
 */
const handleRemoveActiveFilters = () => {
  activeFilters.value.forEach((filter) => {
    handleChange({ values: [], filter });
  });

  activeFilters.value = [];
};

/**
 * Removes a specific active filter from the list
 * @param {Object} filter - The filter to be removed
 */
const handleRemoveActiveFilter = (filter) => {
  remove(activeFilters.value, (item) => item.id === filter.id);
};

/**
 * Handles changes in filter values and updates the filter states
 * @param {Object} param0 - Object containing values and filter
 * @param {Array} param0.values - New values for the filter
 * @param {Object} param0.filter - The filter being changed
 */
const handleChange = ({ values, filter }) => {
  filter.values = values;

  let filterValue = values;

  if (
    filter.id === FilterType.VISIBILITY ||
    filter.id === FilterType.TASK_TYPE
  ) {
    filterValue = values[0];
  }

  handleChangeFilter(
    filter.id === FilterType.SPACES || filter.id === FilterType.ASSETS
      ? FilterType.LINKED_TO
      : filter.id,
    filterValue
  );
};

/**
 * Updates the assets filter based on the provided asset IDs
 * @param {Array} assetIds - Array of asset IDs
 */
const handleUpdateAssets = (assetIds) => {
  let filter = optionGroups
    .flatMap((group) => group.options)
    .find((option) => option.id === FilterType.ASSETS);

  let assetsFilter = activeFilters.value.find(
    (filter) => filter.id === FilterType.ASSETS
  );

  if (assetIds.length) {
    if (!assetsFilter) {
      activeFilters.value.push({
        ...filter,
        open: false,
        values: assetIds,
      });
    } else {
      assetsFilter.values = assetIds;
    }
  } else if (assetsFilter) {
    handleRemoveActiveFilter(assetsFilter);
  }
};

/**
 * Retrieves the current state of a filter
 * @param {string} id - The ID of the filter
 * @returns {Object} The current state of the filter
 */
const getFilterState = (id) => {
  const defaultState = { buildings: [], children: [] };

  if (id === FilterType.ASSET_GROUPS) {
    return {
      ...defaultState,
      children: filterStates.value[id] || [],
    };
  }

  if ([FilterType.SPACES, FilterType.ASSETS].includes(id)) {
    return filterStates.value[FilterType.LINKED_TO] || defaultState;
  }

  return filterStates.value[id];
};

/**
 * Returns the appropriate component for a given filter type
 * @param {string} component - The filter type
 * @returns {Object} The Vue component corresponding to the filter type
 */
const getComponent = (component) => {
  switch (component) {
    case FilterType.SPACES:
      return Space;
    case FilterType.ASSETS:
      return Asset;
    case FilterType.ASSET_GROUPS:
      return AssetGroup;
    case FilterType.ASSIGNED_TO:
      return Assignee;
    case FilterType.PRIORITY:
      return Priority;
    case FilterType.CATEGORY:
      return Category;
    case FilterType.BRAND:
      return Brand;
    case FilterType.SUPPLIER:
      return Supplier;
    case FilterType.TAGS:
      return Tag;
    case FilterType.VISIBILITY:
      return Visibility;
    case FilterType.TASK_TYPE:
      return TaskType;
    case FilterType.DUE_DATE:
      return DueDate;
    case FilterType.COMPLETED_DATE:
      return CompletedDate;
    case FilterType.CREATED_DATE:
      return CreatedDate;
    case FilterType.PURCHASED_DATE:
      return PurchasedDate;
    case FilterType.EXPIRATION_DATE:
      return ExpirationDate;
    case FilterType.NOTIFICATION_DATE:
      return NotificationDate;
    case FilterType.STATUS:
      return Status;
    case FilterType.RECURRENCE:
      return Recurrence;
  }
};

const handleCloseViewFilters = () => {
  emit("closeViewFilters");
};

const handleAddView = (view) => {
  emit("addView", view);
};

const handleUpdateView = (view) => {
  emit("updateView", view);
};

defineExpose({ handleApplyViewFilters });
</script>
